import { z } from 'zod';
import { boxStatusSchema, isoDateSchema, statusChangeTargetSchema, } from '#src/archive/retention.js';
import { pageOf } from '#src/common/pagination.contract.js';
import { organizationCodeSchema } from '#src/organization/organization.contract.js';
export const LOCATION_MAX_LENGTH = 100;
export const STATUS_REASON_MAX_LENGTH = 500;
const locationSchema = z.string().trim().max(LOCATION_MAX_LENGTH);
/** Data final igual ou posterior à inicial (regra R6). */
const periodIsOrdered = (body) => body.periodStart === undefined ||
    body.periodEnd === undefined ||
    body.periodEnd >= body.periodStart;
const PERIOD_ORDER_ISSUE = {
    message: 'A data final deve ser igual ou posterior à inicial',
    path: ['periodEnd'],
};
/**
 * Código da caixa para as pessoas: empresa, estabelecimento e número
 * (`01-31-12`). É o que se fala, digita e imprime. A identidade continua
 * sendo estabelecimento + número (R1): a empresa entra para leitura, e o
 * código é calculado, nunca gravado.
 */
export const boxCodeOf = (box) => `${box.company.code}-${box.establishment.code}-${box.number}`;
export const boxSchema = z.strictObject({
    id: z.uuid(),
    /** `01-31-12`: empresa, estabelecimento e número (ver `boxCodeOf`). */
    code: z.string(),
    number: z.int().positive(),
    /**
     * Empresa dona dos documentos. O nome é o atual no ERP; nulo quando o ERP
     * não respondeu e ainda não havia nome guardado (a tela mostra o código).
     */
    company: z.strictObject({ code: z.string(), name: z.string().nullable() }),
    /**
     * Estabelecimento dono dos documentos. O código é o "EST" da etiqueta, e a
     * numeração das caixas é por estabelecimento.
     */
    establishment: z.strictObject({
        code: z.string(),
        name: z.string().nullable(),
    }),
    category: z.strictObject({ id: z.uuid(), name: z.string() }),
    periodStart: isoDateSchema,
    periodEnd: isoDateSchema,
    /** Data final + 5 anos e 6 meses (regra R7). */
    discardDate: isoDateSchema,
    /** O que a pessoa vê: o status gravado ou o da data de expurgo (R8). */
    status: boxStatusSchema,
    location: z.string().nullable(),
    /** `app`: cadastrada no app. `legacy`: importada da planilha. */
    source: z.enum(['app', 'legacy']),
    /** Controle de concorrência: a edição envia a versão que leu. */
    version: z.int().positive(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
});
/** Um campo alterado, com os valores como a pessoa os via. */
export const fieldChangeSchema = z.strictObject({
    from: z.string().nullable(),
    to: z.string().nullable(),
});
export const boxHistoryEntrySchema = z.strictObject({
    action: z.enum([
        'created',
        'updated',
        'imported',
        // Mudanças de status, pelo status de destino.
        'obsoleted',
        'restored',
        'cancelled',
        'discarded',
    ]),
    at: z.iso.datetime({ offset: true }),
    /** Nome de quem fez. */
    actor: z.string(),
    changes: z.record(z.string(), fieldChangeSchema),
});
/** Quem pôs a caixa no status atual, quando e por quê. */
export const boxStatusNoteSchema = z.strictObject({
    status: boxStatusSchema,
    at: z.iso.datetime({ offset: true }),
    actor: z.string(),
    reason: z.string(),
});
export const boxDetailSchema = z.strictObject({
    box: boxSchema,
    /** Nulo quando a caixa segue a data (nunca marcada, ou de volta a `ok`). */
    statusNote: boxStatusNoteSchema.nullable(),
    /**
     * Status para os quais a caixa pode ir agora. A tela mostra uma ação para
     * cada um, sem repetir as regras de transição.
     */
    transitions: z.array(statusChangeTargetSchema),
    history: z.array(boxHistoryEntrySchema),
});
export const boxPageSchema = pageOf(boxSchema);
export const createBoxRequestSchema = z
    .strictObject({
    companyCode: organizationCodeSchema,
    establishmentCode: organizationCodeSchema,
    categoryId: z.uuid(),
    periodStart: isoDateSchema,
    periodEnd: isoDateSchema,
    location: locationSchema.optional(),
})
    .refine(periodIsOrdered, PERIOD_ORDER_ISSUE);
/**
 * Empresa, estabelecimento e número não mudam (regra R5). `location: null`
 * apaga o local.
 */
export const updateBoxRequestSchema = z
    .strictObject({
    version: z.int().positive(),
    categoryId: z.uuid().optional(),
    periodStart: isoDateSchema.optional(),
    periodEnd: isoDateSchema.optional(),
    location: locationSchema.nullable().optional(),
})
    .refine(periodIsOrdered, PERIOD_ORDER_ISSUE)
    .refine((body) => Object.keys(body).length > 1, 'Informe ao menos um campo para alterar');
const reasonSchema = z.string().trim().min(1).max(STATUS_REASON_MAX_LENGTH);
/** Muda o status, sempre com motivo: marcar obsoleta, voltar a OK, cancelar. */
export const changeBoxStatusRequestSchema = z.strictObject({
    version: z.int().positive(),
    status: statusChangeTargetSchema,
    reason: reasonSchema,
});
/**
 * Formato da etiqueta: o PDF para ver e imprimir; o .docx, o mesmo conteúdo
 * para quem precisa ajustar no Word.
 */
export const labelFormatSchema = z.enum(['pdf', 'docx']);
/**
 * Caixas por pedido nas ações em lote (etiquetas, descarte): nas etiquetas,
 * até 10 folhas, duas caixas por folha.
 */
export const BOX_BATCH_MAX = 20;
/**
 * Registra o descarte de caixas obsoletas, com a justificativa (R10). Cada
 * caixa vai com a versão que a pessoa viu: se alguma mudou, nenhuma é
 * descartada.
 */
export const discardBoxesRequestSchema = z.strictObject({
    boxes: z
        .array(z.strictObject({ id: z.uuid(), version: z.int().positive() }))
        .min(1)
        .max(BOX_BATCH_MAX)
        .refine((boxes) => new Set(boxes.map((box) => box.id)).size === boxes.length, 'Caixa repetida no pedido'),
    reason: reasonSchema,
});
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
/**
 * `?ids=<id>,<id>`: as caixas, na ordem em que saem nas folhas. Texto, sem
 * transformar, para a documentação da API; `labelIdsOf` separa os ids.
 */
export const boxLabelsQuerySchema = z.strictObject({
    ids: z
        .string()
        .regex(new RegExp(`^${UUID}(,${UUID})*$`, 'i'), 'Ids separados por vírgula')
        .refine((ids) => ids.split(',').length <= BOX_BATCH_MAX, `No máximo ${BOX_BATCH_MAX} caixas por vez`),
});
export const labelIdsOf = (query) => query.ids.split(',');
//# sourceMappingURL=box.contract.js.map